/*
  Voice capture button.

  Uses the native Web Speech API when available (Chrome desktop, Edge, Android
  Chrome). Falls back to MediaRecorder → POST /api/stt when Web Speech isn't
  exposed (iOS Safari, Firefox desktop). On iOS the first tap will prompt for
  mic permission — subsequent taps are silent.

  Fires `onResult(text)` when a transcript is ready. Fires `onInterim(text)`
  during live dictation (native path only).
*/
import { useEffect, useRef, useState } from "react";
import { Mic, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import { phase2Api } from "../lib/phase2Api";

const MAX_RECORD_SECONDS = 45;

function hasWebSpeech() {
  return typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
}

export default function VoiceCaptureButton({ onResult, onInterim, disabled }) {
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const mediaRecRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const autoStopRef = useRef(null);

  useEffect(() => () => {
    try { recRef.current?.stop?.(); } catch { /* noop */ }
    try { mediaRecRef.current?.stop?.(); } catch { /* noop */ }
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
  }, []);

  // --- Web Speech API path ---------------------------------------------------
  function startWebSpeech() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";
    let finalText = "";

    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t;
        else interim += t;
      }
      if (interim && onInterim) onInterim(interim);
      if (finalText && onResult) onResult(finalText.trim());
    };
    rec.onerror = (e) => {
      // "no-speech" just means the user didn't say anything — not an error worth toasting.
      if (e.error && e.error !== "no-speech" && e.error !== "aborted") {
        toast.error(`Voice: ${e.error}`);
      }
    };
    rec.onend = () => setListening(false);

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
      autoStopRef.current = setTimeout(() => {
        try { rec.stop(); } catch { /* noop */ }
      }, MAX_RECORD_SECONDS * 1000);
    } catch (err) {
      toast.error("Couldn't start voice input");
      setListening(false);
    }
  }

  // --- MediaRecorder → Whisper fallback -------------------------------------
  async function startMediaRecorder() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Browsers pick whichever of these they support
      const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mimeType = mimeCandidates.find(m => MediaRecorder.isTypeSupported?.(m)) || "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) mediaChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        setListening(false);
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(mediaChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size === 0) return;
        setBusy(true);
        try {
          const { text } = await phase2Api.transcribe(blob);
          if (text && onResult) onResult(text.trim());
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Transcription failed");
        } finally {
          setBusy(false);
        }
      };
      mr.start();
      mediaRecRef.current = mr;
      setListening(true);
      autoStopRef.current = setTimeout(() => { try { mr.stop(); } catch { /* noop */ } }, MAX_RECORD_SECONDS * 1000);
    } catch (err) {
      toast.error("Microphone blocked — enable in browser settings");
    }
  }

  function stop() {
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    try { recRef.current?.stop?.(); } catch { /* noop */ }
    try { mediaRecRef.current?.stop?.(); } catch { /* noop */ }
  }

  function toggle() {
    if (listening) { stop(); return; }
    if (hasWebSpeech()) startWebSpeech();
    else startMediaRecorder();
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled || busy}
      data-testid="voice-capture"
      title={listening ? "Stop" : "Voice input"}
      className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors shrink-0 ${
        listening
          ? "bg-[color:var(--accent)] text-white"
          : "hover:bg-[color:var(--surface-2)]"
      } disabled:opacity-40`}
      aria-pressed={listening}
      aria-label={listening ? "Stop voice input" : "Start voice input"}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" />
        : listening ? <Square className="h-4 w-4" />
        : <Mic className="h-4 w-4" />}
    </button>
  );
}
